/**
 * ASIASTYLE-R7.1 — on-site recommendations client (R6.8 brain via Worker).
 */
import { waitForTelegramInitData } from "../lib/telegram.js";
import { storeV2AuthHeaders } from "./store-v2-auth-headers-v2.js";
import { fetchWithTimeout } from "./fetch-timeout-v2.js";
import type { CatalogV2Product } from "./catalog-v2-types.js";
import type { CrossSellCandidate } from "./cross-sell-rules.js";
import { resolveProductTitle } from "./product-title-resolver.js";
import type { StoreV2Lang } from "./store-v2-locale.js";

export type OnsiteSurface = "HOME_FOR_YOU" | "PRODUCT_CROSS_SELL" | "CART_CROSS_SELL";

export type OnsiteRecommendationApiItem = {
  product_id: string;
  title: string | null;
  current_price_uah: number | null;
  stock_state: string | null;
  rank: number;
  recommendation_type: string;
  source_type: string;
  reason_hint_uk: string | null;
  deep_link: string;
};

export type OnsiteRecommendationsResponse = {
  ok: boolean;
  enabled?: boolean;
  hidden?: boolean;
  surface?: string;
  title_uk?: string;
  subtitle_uk?: string | null;
  recommendation_type?: string;
  items?: OnsiteRecommendationApiItem[];
  release?: string;
};

function readTelegramUserId(): string | null {
  try {
    const tg = (window as unknown as {
      Telegram?: { WebApp?: { initDataUnsafe?: { user?: { id?: number | string } } } };
    }).Telegram?.WebApp;
    const id = tg?.initDataUnsafe?.user?.id;
    return id != null ? String(id) : null;
  } catch {
    return null;
  }
}

export async function fetchOnsiteRecommendationFlags(): Promise<{ onsite_enabled: boolean }> {
  try {
    await waitForTelegramInitData(1500);
    const res = await fetchWithTimeout("/api/store-v2/recommendations/onsite/flags", {
      method: "GET",
      headers: storeV2AuthHeaders({ Accept: "application/json" }),
    }, 6000);
    if (!res.ok) return { onsite_enabled: false };
    const data = (await res.json()) as { onsite_enabled?: boolean };
    return { onsite_enabled: Boolean(data.onsite_enabled) };
  } catch {
    return { onsite_enabled: false };
  }
}

export async function fetchOnsiteRecommendations(input: {
  surface: OnsiteSurface;
  anchorProductId?: string | null;
  cartProductIds?: string[];
  limit?: number;
}): Promise<OnsiteRecommendationsResponse> {
  try {
    await waitForTelegramInitData(2000);
    const params = new URLSearchParams();
    params.set("surface", input.surface);
    const tg = readTelegramUserId();
    if (tg) params.set("telegram_user_id", tg);
    if (input.anchorProductId) params.set("anchor_product_id", input.anchorProductId);
    if (input.cartProductIds?.length) params.set("cart_product_ids", input.cartProductIds.join(","));
    if (input.limit) params.set("limit", String(input.limit));
    const res = await fetchWithTimeout(
      `/api/store-v2/recommendations/onsite?${params.toString()}`,
      { method: "GET", headers: storeV2AuthHeaders({ Accept: "application/json" }) },
      8000,
    );
    if (!res.ok) return { ok: false, enabled: false };
    return (await res.json()) as OnsiteRecommendationsResponse;
  } catch {
    return { ok: false, enabled: false };
  }
}

export async function validateOnsiteAdd(productId: string): Promise<{
  ok: boolean;
  message_uk?: string;
  current_price_uah?: number;
}> {
  try {
    await waitForTelegramInitData(1500);
    const res = await fetchWithTimeout(
      "/api/store-v2/recommendations/onsite/validate-add",
      {
        method: "POST",
        headers: storeV2AuthHeaders({ Accept: "application/json", "Content-Type": "application/json" }),
        body: JSON.stringify({ product_id: productId }),
      },
      6000,
    );
    const data = (await res.json()) as { ok?: boolean; message_uk?: string; current_price_uah?: number };
    const out: { ok: boolean; message_uk?: string; current_price_uah?: number } = { ok: Boolean(data.ok) };
    if (data.message_uk) out.message_uk = data.message_uk;
    if (data.current_price_uah != null) out.current_price_uah = data.current_price_uah;
    return out;
  } catch {
    return { ok: false, message_uk: "Цей товар щойно став недоступним." };
  }
}

export function shellFromOnsiteItem(
  item: OnsiteRecommendationApiItem,
  catalogProduct?: CatalogV2Product | null,
): CatalogV2Product {
  if (catalogProduct) return catalogProduct;
  const price = Number(item.current_price_uah ?? 0);
  const available = item.stock_state !== "out_of_stock" && price > 0;
  const title = item.title ?? item.product_id;
  const looksProm = /^\d{6,}$/.test(String(item.product_id ?? ""));
  return {
    id: item.product_id,
    sku: item.product_id,
    title,
    titleRu: null,
    categoryKeys: [],
    categoryName: "",
    imageUrl: null,
    productUrl: null,
    priceUah: price > 0 ? price : null,
    displayPrice: price > 0 ? `${price} грн` : "",
    isPriced: price > 0,
    stockStatus: item.stock_state ?? "unknown",
    isSellable: available,
    sellabilityReason: available ? "available" : "unavailable",
    brand: null,
    country: null,
    weight: null,
    regularPriceUah: null,
    salePriceUah: null,
    saleStartsAt: null,
    saleEndsAt: null,
    promotionId: null,
    promotionStatus: null,
    priceUpdatedAt: null,
    sourceRevision: null,
    search: title.toLowerCase(),
    promProductId: looksProm ? item.product_id : null,
    merchantSku: looksProm ? null : item.product_id,
    merchantArticle: null,
    barcodeGtin: null,
    barcodeVerification: "absent",
    manufacturerPartNumber: null,
    warehouseCode: null,
    sourceSystem: "onsite_r71",
    sourceUpdatedAt: null,
    lastSyncedAt: null,
    firstSeenAt: null,
    catalogArrivedAt: null,
    sourceTitle: title,
    customerTitle: title,
    titleAliases: [],
    originalSkuVerified: false,
    nutrition: null,
    maxOrderQty: null,
  };
}

export function mapOnsiteToCrossSell(
  items: OnsiteRecommendationApiItem[],
  catalogById: Map<string, CatalogV2Product>,
  lang: StoreV2Lang,
): CrossSellCandidate[] {
  const out: CrossSellCandidate[] = [];
  for (const item of items) {
    const catalog = catalogById.get(item.product_id);
    const product = shellFromOnsiteItem(item, catalog);
    if (!product.isSellable || product.priceUah == null) continue;
    out.push({
      product,
      title: resolveProductTitle(product, lang).title,
      reasonCategory: item.reason_hint_uk ?? item.source_type,
    });
  }
  return out;
}
